import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { unwrapApiData } from "../../api/unwrap";
import {
  countBrowserPosPending,
  flushBrowserPosQueue,
} from "../../lib/pos-browser-offline";
import { useAuth } from "../../context/AuthContext";

async function postCollectBody(body: Record<string, unknown>) {
  const res = await api.post<{ ok: true; data: unknown }>("/v1/pos/collect", body);
  return unwrapApiData(res);
}

/** Background sync for browser-queued POS sales (no Local Engine required). */
export function PosOfflineSync() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [pending, setPending] = useState(0);
  const [lastSync, setLastSync] = useState<string | null>(null);

  const refreshPending = useCallback(async () => {
    try {
      setPending(await countBrowserPosPending());
    } catch {
      setPending(0);
    }
  }, []);

  const runFlush = useCallback(async () => {
    if (!isAuthenticated || !navigator.onLine) return;
    try {
      const { discoverLocalEngineBaseUrl, pingLocalEngine, triggerLocalSync } =
        await import("../../lib/local-engine");
      const base = await discoverLocalEngineBaseUrl();
      if (await pingLocalEngine(base)) {
        await triggerLocalSync(base).catch(() => undefined);
      }
      const result = await flushBrowserPosQueue(postCollectBody);
      await refreshPending();
      if (result.synced > 0) {
        setLastSync(
          t("pos.browser_sync_done", {
            defaultValue: "تمت مزامنة {{n}} عملية POS من المتصفح.",
            n: result.synced,
          }),
        );
      }
    } catch {
      /* ignore */
    }
  }, [isAuthenticated, refreshPending, t]);

  useEffect(() => {
    void refreshPending();
  }, [refreshPending]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void runFlush();
    const onOnline = () => void runFlush();
    window.addEventListener("online", onOnline);
    const interval = window.setInterval(() => void runFlush(), 60_000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.clearInterval(interval);
    };
  }, [isAuthenticated, runFlush]);

  if (!isAuthenticated || pending === 0) {
    if (lastSync) {
      return (
        <div className="fixed bottom-3 left-3 z-[100] max-w-sm rounded-lg bg-emerald-800 px-3 py-2 text-xs text-white shadow-lg">
          {lastSync}
        </div>
      );
    }
    return null;
  }

  return (
    <div
      className="fixed bottom-3 left-3 z-[100] max-w-sm rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950 shadow-lg"
      dir="rtl"
    >
      <p className="font-semibold">
        {t("pos.browser_queue_pending", {
          defaultValue: "{{n}} عملية POS محفوظة في المتصفح — بانتظار الإنترنت.",
          n: pending,
        })}
      </p>
      <button
        type="button"
        className="mt-1 font-bold text-godo-accent underline"
        onClick={() => void runFlush()}
      >
        {t("pos.browser_sync_now", { defaultValue: "مزامنة الآن" })}
      </button>
    </div>
  );
}
